import { Boxes, Check, Copy, Search, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/app/components/ui/popover";

import { calculateFitCount } from "./deviceUtils";

export interface ApplicationsCellProps {
  readonly apps: readonly string[];
  readonly searchPlaceholder?: string;
}

export function ApplicationsCell({ apps, searchPlaceholder = "Search applications..." }: ApplicationsCellProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(1);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [copiedApp, setCopiedApp] = useState<string | null>(null);

  const recompute = useCallback(() => {
    if (!containerRef.current || !measureRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    if (containerWidth <= 0) return;

    const measureChildren = Array.from(measureRef.current.children) as HTMLElement[];
    const itemElements = measureChildren.slice(0, apps.length);
    const badgeElement = measureChildren[measureChildren.length - 1];
    const badgeWidth = badgeElement ? badgeElement.offsetWidth : 64;
    const itemWidths = itemElements.map((el) => el.offsetWidth || 60);

    const nextCount = calculateFitCount(containerWidth, itemWidths, badgeWidth, 6);
    setVisibleCount((prev) => (prev !== nextCount ? nextCount : prev));
  }, [apps]);

  useEffect(() => {
    recompute();
    if (!containerRef.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      recompute();
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [recompute]);

  const visibleApps = apps.slice(0, visibleCount);
  const remainingCount = apps.length - visibleCount;

  const filteredApps = useMemo(() => {
    if (!search.trim()) return apps;
    const query = search.toLowerCase();
    return apps.filter((app) => app.toLowerCase().includes(query));
  }, [apps, search]);

  const handleCopy = (e: React.MouseEvent, appName: string) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(appName);
    setCopiedApp(appName);
    setTimeout(() => {
      setCopiedApp((curr) => (curr === appName ? null : curr));
    }, 1500);
  };

  return (
    <>
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible fixed top-[-9999px] left-[-9999px] flex items-center gap-1.5 whitespace-nowrap opacity-0"
      >
        {apps.map((app, idx) => (
          <span
            key={`measure-${app}-${idx}`}
            className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border whitespace-nowrap"
          >
            {app}
          </span>
        ))}
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-semibold border whitespace-nowrap">
          +{apps.length} more
        </span>
      </div>

      <div
        ref={containerRef}
        className="relative w-full max-w-full flex items-center gap-1.5 overflow-hidden"
      >
        {visibleApps.map((app, idx) => (
          <span
            key={`visible-${app}-${idx}`}
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-muted/70 text-foreground border border-border/60 ${
              visibleApps.length === 1 && remainingCount > 0
                ? "truncate min-w-0 flex-1"
                : "truncate shrink-0 max-w-[140px]"
            }`}
            title={app}
          >
            {app}
          </span>
        ))}

        {remainingCount > 0 && (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-semibold bg-primary/10 hover:bg-primary/20 text-primary border border-primary/25 transition-all cursor-pointer shrink-0 select-none shadow-xs active:scale-95"
                title={`Click to view all ${apps.length} applications`}
              >
                +{remainingCount} more
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="bottom"
              sideOffset={6}
              className="w-80 p-0 shadow-xl border border-border bg-popover text-popover-foreground rounded-lg z-50 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-2.5 bg-muted/40 border-b border-border flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Boxes className="size-4 text-primary shrink-0" />
                  <span className="text-xs font-semibold text-foreground">
                    Applications
                  </span>
                  <span className="text-[11px] px-1.5 py-0.2 rounded-full font-medium bg-primary/15 text-primary">
                    {apps.length}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setPopoverOpen(false)}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                  aria-label="Close"
                >
                  <X className="size-3.5" />
                </button>
              </div>

              {apps.length > 4 && (
                <div className="p-2 border-b border-border/60 bg-background/50">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={searchPlaceholder}
                      className="w-full pl-7 pr-7 py-1 text-xs bg-muted/40 border border-border rounded-md placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-primary/40 focus:border-primary/50 text-foreground"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="size-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="max-h-60 overflow-y-auto p-1.5 space-y-0.5">
                {filteredApps.length === 0 ? (
                  <div className="py-4 text-center text-xs text-muted-foreground">
                    No matching applications
                  </div>
                ) : (
                  filteredApps.map((app, index) => {
                    const isCopied = copiedApp === app;
                    return (
                      <div
                        key={`popover-${app}-${index}`}
                        className="group flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-xs hover:bg-muted/70 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="size-1.5 rounded-full bg-primary/60 shrink-0" />
                          <span
                            className="truncate text-foreground font-normal"
                            title={app}
                          >
                            {app}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => handleCopy(e, app)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-foreground rounded transition-opacity shrink-0 cursor-pointer hover:bg-muted"
                          title="Copy name"
                        >
                          {isCopied ? (
                            <Check className="size-3 text-emerald-500" />
                          ) : (
                            <Copy className="size-3" />
                          )}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="px-3 py-1.5 bg-muted/20 border-t border-border/50 text-[11px] text-muted-foreground flex items-center justify-between">
                <span>
                  {search
                    ? `${filteredApps.length} of ${apps.length} applications`
                    : `${apps.length} applications total`}
                </span>
                <span className="text-[10px] text-muted-foreground/75">
                  Click icon to copy
                </span>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </>
  );
}
